// Peças de formulário sem domínio: campo com etiqueta/ajuda/erro, aviso e interruptor.
import { cva, type VariantProps } from "class-variance-authority";
import { Switch as SwitchPrimitive } from "radix-ui";
import { useId, type ComponentProps, type ReactNode } from "react";
import { cn } from "./cn";
import { Input } from "./input";
import { Label } from "./label";

/** Campo de texto completo: <Field label="Email" type="email" hint="…" error="…" /> */
export function Field({ label, hint, error, className, id, ...props }: ComponentProps<typeof Input> & { label: ReactNode; hint?: ReactNode; error?: ReactNode }) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <Label htmlFor={fid}>{label}</Label>
      <Input id={fid} aria-invalid={!!error || undefined} aria-describedby={hint || error ? `${fid}-help` : undefined} {...props} />
      {(error || hint) && (
        <p id={`${fid}-help`} className={cn("text-xs", error ? "text-destructive" : "text-muted-foreground")}>
          {error ?? hint}
        </p>
      )}
    </div>
  );
}

const alertVariants = cva("rounded-md border px-4 py-3 text-sm", {
  variants: {
    variant: {
      info: "border-border bg-muted text-foreground",
      success: "border-success/40 bg-success/10 text-success",
      warning: "border-warning/40 bg-warning/10 text-warning",
      destructive: "border-destructive/40 bg-destructive/10 text-destructive",
    },
  },
  defaultVariants: { variant: "info" },
});

export function Alert({ className, variant, ...props }: ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  return <div role={variant === "destructive" ? "alert" : "status"} className={cn(alertVariants({ variant }), className)} {...props} />;
}

export function Switch({ className, ...props }: ComponentProps<typeof SwitchPrimitive.Root>) {
  return (
    <SwitchPrimitive.Root
      className={cn(
        "peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border border-input bg-muted transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-gold",
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb className="pointer-events-none block size-5 translate-x-0.5 rounded-full bg-parchment shadow transition-transform data-[state=checked]:translate-x-5 data-[state=checked]:bg-night" />
    </SwitchPrimitive.Root>
  );
}
